import React from 'react';
import { motion } from 'framer-motion';
import { Users, ArrowRight, UserRound, FileText, MessageSquare, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../PageHeader';
import { teamSites, TeamSite } from '../data';
/* The concept spotlights one team in a rich hero panel, with the remaining
   teams below as a compact secondary grid. We feature the first team in the
   directory. */
function FeaturedTeam({
  team
}: {
  team: TeamSite;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="bg-white rounded-2xl border border-navy-100 shadow-sm overflow-hidden" data-id="element-835">
      <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-836">
        {/* Left — identity, description, quick links, CTA */}
        <div className="p-6 sm:p-8" data-id="element-837">
          <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wide text-gold-700 bg-gold-50 px-2.5 py-1 rounded-full mb-4" data-id="element-838">
            Featured team
          </span>
          <h2 className="text-2xl font-serif font-bold text-navy-900" data-id="element-839">
            {team.name}
          </h2>
          <div className="flex items-center gap-5 mt-2 text-sm text-navy-500 font-medium" data-id="element-840">
            <span className="inline-flex items-center gap-1.5" data-id="element-841">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" data-id="element-842" />
              {team.members} members
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-843">
              <span className="w-1.5 h-1.5 rounded-full bg-sage" data-id="element-844" />
              Active today
            </span>
          </div>
          <p className="text-navy-600 leading-relaxed mt-4 max-w-md" data-id="element-845">
            {team.description}
          </p>

          <div className="flex flex-wrap gap-2 mt-5" data-id="element-846">
            {[{
            label: 'Files',
            icon: FileText
          }, {
            label: 'Discussions',
            icon: MessageSquare
          }, {
            label: 'Events',
            icon: Calendar
          }].map(({
            label,
            icon: Icon
          }) => <Link key={label} to={`/teams/${team.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 bg-navy-50 border border-navy-100 px-3 py-1.5 rounded-lg hover:bg-navy-100 hover:border-navy-200 transition-colors" data-id="element-847">
                <Icon className="w-3.5 h-3.5 text-navy-400" data-id="element-848" />
                {label}
              </Link>)}
          </div>

          <Link to={`/teams/${team.id}`} className="inline-flex items-center gap-2 mt-6 text-sm font-bold text-white bg-navy-600 px-5 py-2.5 rounded-lg hover:bg-navy-700 transition-colors group" data-id="element-849">
            Open {team.name}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-850" />
          </Link>
        </div>

        {/* Right — brand strip, member cluster, activity summary */}
        <div className="p-6 sm:p-8 bg-navy-50/50 border-t lg:border-t-0 lg:border-l border-navy-100 flex flex-col justify-center gap-5" data-id="element-851">
          <div className={`h-2 w-full rounded-full ${team.color}`} data-id="element-852" />

          <div className="flex items-center gap-3" data-id="element-853">
            <span className="text-xs font-semibold text-navy-500 w-20 shrink-0" data-id="element-854">
              Members
            </span>
            <div className="flex items-center" data-id="element-855">
              {Array.from({
              length: Math.min(team.members, 4)
            }).map((_, i) => <div key={i} className="w-8 h-8 rounded-full bg-white text-navy-300 flex items-center justify-center border-2 border-white ring-1 ring-navy-100 -ml-2 first:ml-0" aria-hidden="true" data-id="element-856">
                  <UserRound className="w-4 h-4" data-id="element-857" />
                </div>)}
              {team.members > 4 && <div className="w-8 h-8 rounded-full bg-navy-100 text-navy-600 flex items-center justify-center text-[0.65rem] font-bold border-2 border-white -ml-2" data-id="element-858">
                  +{team.members - 4}
                </div>}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-navy-100 p-3.5 text-sm text-navy-600 leading-relaxed" data-id="element-859">
            <span className="font-bold text-navy-800" data-id="element-860">Latest activity</span> ·
            recent files, discussions, and upcoming syncs appear here once this
            team’s workspace is connected.
          </div>
        </div>
      </div>
    </motion.div>;
}
function TeamCard({
  team,
  index
}: {
  team: TeamSite;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.04
  }} data-id="element-861">
      <Link to={`/teams/${team.id}`} className="group flex flex-col gap-3 h-full bg-white rounded-xl border border-navy-100 p-4 hover:border-navy-200 hover:shadow-md transition-all" data-id="element-862">
        <div className="flex items-start gap-3" data-id="element-863">
          <span className={`w-3 h-3 rounded-full shrink-0 mt-1.5 ${team.color}`} aria-hidden="true" data-id="element-864" />
          <h3 className="flex-1 font-serif font-bold text-base text-navy-900 leading-snug" data-id="element-865">
            {team.name}
          </h3>
          <span className="text-[0.7rem] font-semibold text-navy-500 bg-navy-50 px-2 py-1 rounded whitespace-nowrap" data-id="element-866">
            {team.members}
          </span>
        </div>
        <p className="flex-1 text-xs text-navy-500 leading-relaxed line-clamp-3" data-id="element-867">
          {team.description}
        </p>
        <div className="flex items-center justify-between pt-3 border-t border-navy-50" data-id="element-868">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-gold-600 group-hover:gap-2 transition-all" data-id="element-869">
            Open <ArrowRight className="w-3.5 h-3.5" data-id="element-870" />
          </span>
        </div>
      </Link>
    </motion.div>;
}
export function TeamsPage() {
  const [featured, ...rest] = teamSites;
  return <>
      <PageHeader title="Teams & Sites" subtitle="Spaces for every team to share news, files, and discussion." data-id="element-871" />

      {featured && <div className="mb-8" data-id="element-872">
          <FeaturedTeam team={featured} data-id="element-873" />
        </div>}

      <h2 className="text-xs font-bold uppercase tracking-wide text-navy-400 mb-3" data-id="element-874">
        Other teams
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-875">
        {rest.map((t, i) => <TeamCard key={t.id} team={t} index={i} data-id="element-876" />)}
      </div>
    </>;
}